import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import useReveal from "../lib/useReveal";
import Seo from "../components/Seo";
import { WHY_US, CLIENTS, CERTIFICATIONS } from "../lib/services";

export default function About() {
    useReveal();
    useEffect(() => window.scrollTo(0, 0), []);

    return (
        <main data-testid="about-page" className="pt-40 md:pt-52">
            <Seo
                title="About Us — Crafting Memories & Nourishing Lives | The Food Gala"
                description="Since 2019, The Food Gala has been the devoted catering partner for thousands of corporates, hospitals, students and event hosts across Cuttack & Bhubaneswar. FSSAI, ISO 9001:2015 and Zed Silver certified. Read our mission, vision and meet our clients."
                path="/about"
            />
            {/* HEADER */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-12 md:pb-20">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-6">
                            About · The Food Gala
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tight">
                            Crafting memories.
                            <br />
                            <em className="not-italic text-[#1B4332]">Nourishing lives.</em>
                        </h1>
                    </div>
                </div>
            </section>

            {/* STORY + IMAGE */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-10 md:pb-12" data-testid="about-story">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-7 reveal">
                        <div className="zoom-wrap aspect-[5/4]">
                            <img
                                src="/brochure/about_us.png"
                                alt="The Food Gala — banquet spread with elegant table setting"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-5 reveal">
                        <p className="font-display text-3xl md:text-4xl leading-[1.1] text-[#1A1A1A]">
                            We transcend being <em className="not-italic text-[#1B4332]">mere meal service providers.</em>
                        </p>
                        <p className="mt-6 text-lg font-light text-[#1A1A1A]/75 leading-relaxed">
                            We are a devoted partner in delivering delectable and nourishing meals
                            since 2019. Our clientele has increased steadily from a few to few
                            thousands over the years. We cater to a diverse range of clients —
                            corporates, hospitals, senior citizens, students and party hosts. Our
                            steadfast dedication to quality, customization and genuine care along
                            with reliability ensures that culinary excellence arrives right at your
                            doorstep.
                        </p>
                    </div>
                </div>
            </section>

            {/* CERTIFICATION OF EXCELLENCE */}
            <section
                className="bg-[#F3F1EC] py-10 md:py-14"
                data-testid="certification-of-excellence"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="reveal text-center md:text-left">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-4">
                            Marks of Trust
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
                            Certification of <em className="not-italic text-[#1B4332]">Excellence.</em>
                        </h2>
                        <p className="mt-6 max-w-2xl text-lg font-light text-[#1A1A1A]/75">
                            Independently audited and certified — every kitchen, every plate, every
                            promise.
                        </p>
                    </div>

                    <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {CERTIFICATIONS.map((c) => (
                            <div
                                key={c.name}
                                data-testid={`about-cert-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="reveal bg-[#FAF9F6] border border-[#1B4332]/15 p-5 md:p-6 flex flex-col items-center text-center hover:border-[#1B4332] hover:-translate-y-1 transition-all duration-500"
                            >
                                <h3 className="font-display font-bold text-2xl md:text-3xl text-[#1A1A1A] leading-tight tracking-tight">
                                    {c.name}
                                </h3>
                                <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-[#1B4332]/70">
                                    {c.full}
                                </div>
                                <div className="mt-4 w-40 h-40 md:w-48 md:h-48 grid place-items-center bg-[#FAF9F6]">
                                    <img
                                        src={c.image}
                                        alt={`${c.name} certification — The Food Gala`}
                                        loading="lazy"
                                        className="max-h-full max-w-full object-contain"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUR CLIENTS */}
            <section className="bg-[#FAF9F6] py-10 md:py-14" data-testid="our-clients-section">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="reveal text-center md:text-left">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-4">
                            Clientele
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
                            Our <em className="not-italic text-[#1B4332]">Clients.</em>
                        </h2>
                        <p className="mt-4 max-w-2xl text-lg font-light text-[#1A1A1A]/75">
                            Teams and institutions who entrust their everyday meals — and their most
                            important moments — to us.
                        </p>
                    </div>

                    <div className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-[#1B4332]/15">
                        {CLIENTS.map((c) => (
                            <div
                                key={c.name}
                                data-testid={`client-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="bg-[#FAF9F6] py-8 md:py-10 px-6 flex flex-col items-center justify-center text-center reveal hover:bg-[#F3F1EC] transition-colors duration-500 min-h-[200px]"
                            >
                                <div className="w-24 h-24 md:w-28 md:h-28 grid place-items-center bg-[#FAF9F6]">
                                    {c.logo ? (
                                        <img
                                            src={c.logo}
                                            alt={`${c.name} logo`}
                                            className="max-h-full max-w-full object-contain"
                                        />
                                    ) : (
                                        <div className="font-display italic text-3xl text-[#1B4332]/70 select-none">
                                            {c.name.split(" ").map((w) => w[0]).join("")}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-4 font-display font-semibold text-xl md:text-2xl text-[#1A1A1A]">
                                    {c.name}
                                </div>
                                <div className="mt-1 text-[10px] uppercase tracking-[0.32em] text-[#1B4332]/65">
                                    Long-standing partner
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MISSION & VISION */}
            <section className="bg-[#1B4332] text-[#FAF9F6] py-10 md:py-14" data-testid="mission-vision">
                <div className="max-w-[820px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8 md:gap-12">
                    <div className="reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#FFD9A8] mb-4">Mission</div>
                        <p className="font-display text-xl md:text-2xl leading-[1.2]">
                            To nourish bodies, create cherished memories, and foster well-being through
                            culinary excellence and unwavering commitment to our clients.
                        </p>
                    </div>
                    <div className="reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#FFD9A8] mb-4">Vision</div>
                        <p className="font-display text-xl md:text-2xl leading-[1.2]">
                            To be the preferred partner for meal services — renowned for innovation,
                            quality and heartfelt service.
                        </p>
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE US */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="why-choose-us">
                <div className="grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-4 reveal">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-4">Why us</div>
                        <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight">
                            Five reasons our clients return.
                        </h2>
                    </div>
                    <div className="md:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-8">
                        {WHY_US.map((w, i) => (
                            <div key={w.title} className="reveal" data-testid={`why-card-${i}`}>
                                <div className="flex items-baseline gap-3 mb-2">
                                    <span className="font-display italic text-2xl text-[#C84B31]">0{i + 1}</span>
                                    <h3 className="font-display text-2xl md:text-3xl">{w.title}</h3>
                                </div>
                                <p className="text-base font-light text-[#1A1A1A]/75 leading-relaxed">{w.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TEAM TEXT + CTA */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-2 pb-10 md:pb-14 reveal">
                <div className="grid md:grid-cols-12 gap-6 items-center">
                    <div className="md:col-span-7">
                        <div className="text-[11px] uppercase tracking-[0.32em] text-[#C84B31] mb-3">Our Team</div>
                        <h3 className="font-display text-3xl md:text-5xl leading-[1.02]">
                            Culinary experts, nutritionists, and logistics professionals — working
                            harmoniously to deliver the Food Gala experience.
                        </h3>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <Link
                            to="/enquiry"
                            data-testid="about-bottom-cta"
                            className="inline-flex items-center gap-3 bg-[#1B4332] text-[#FAF9F6] px-8 py-4 text-[12px] uppercase tracking-[0.26em] hover:bg-[#112d21] hover:-translate-y-[2px] transition-all duration-300"
                        >
                            Start a Conversation
                            <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.28em] text-[#1B4332]">
                    {["Personalised", "Diverse", "Healthful", "Punctual", "Certified"].map((t) => (
                        <span key={t} className="border border-[#1B4332]/30 px-4 py-2 inline-flex items-center gap-2">
                            <Check size={12} /> {t}
                        </span>
                    ))}
                </div>
            </section>
        </main>
    );
}
